import { MessageSquareIcon, RefreshCwIcon } from "lucide-react";
import { useId, useState } from "react";
import type {
  CmmdTaskComment,
  CmmdTaskConversation,
  CmmdTaskConversationSummary,
} from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import type { ResolvedTaskThreadHistoryGroup } from "~/lib/taskThreadLink";
import type { SidebarThreadSummary } from "~/types";
import { ProjectTaskCommentRow } from "./ProjectTaskCommentRow";

export interface ProjectTaskActivityProps {
  readonly comments: readonly CmmdTaskComment[];
  readonly threadHistory: readonly ResolvedTaskThreadHistoryGroup[];
  readonly conversations: readonly CmmdTaskConversationSummary[];
  readonly conversationsNextCursor: string | null;
  readonly selectedConversation: CmmdTaskConversation | null;
  readonly expandedConversationId: string | null;
  readonly conversationsLoaded: boolean;
  readonly conversationsLoading: boolean;
  readonly loadingMoreConversations: boolean;
  readonly loadingConversationId: string | null;
  readonly loadingMoreConversationEvents: boolean;
  readonly conversationsError: string | null;
  readonly conversationDetailError: string | null;
  readonly loading: boolean;
  readonly commenting: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly onEnsureConversationsLoaded: () => void;
  readonly onRetryConversations: () => void;
  readonly onLoadMoreConversations: () => void;
  readonly onCreateComment: (content: string) => Promise<void>;
  readonly onUpdateComment: (commentKey: string, content: string) => Promise<void>;
  readonly onDeleteComment: (commentKey: string) => Promise<void>;
  readonly onStartWork: () => void;
  readonly onLoadConversation: (conversationId: string) => void;
  readonly onLoadMoreConversationEvents: () => void;
  readonly onClearConversation: () => void;
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
}

export function ProjectTaskActivity(props: ProjectTaskActivityProps) {
  const id = useId();
  const headingId = `${id}-activity-heading`;

  return (
    <section aria-labelledby={headingId} className="scroll-mt-12 space-y-3">
      <h2
        id={headingId}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        Activity
        {props.loading ? null : ` (${props.comments.length})`}
      </h2>

      {props.error ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-md border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive"
        >
          <span>{props.error}</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={RESPONSIVE_TEXT_BUTTON_CLASS}
            onClick={props.onRetry}
          >
            <RefreshCwIcon aria-hidden />
            Retry
          </Button>
        </div>
      ) : null}

      <CommentsPanel
        comments={props.comments}
        loading={props.loading}
        commenting={props.commenting}
        onCreateComment={props.onCreateComment}
        onUpdateComment={props.onUpdateComment}
        onDeleteComment={props.onDeleteComment}
      />
    </section>
  );
}

function CommentsPanel(props: {
  readonly comments: readonly CmmdTaskComment[];
  readonly loading: boolean;
  readonly commenting: boolean;
  readonly onCreateComment: (content: string) => Promise<void>;
  readonly onUpdateComment: (commentKey: string, content: string) => Promise<void>;
  readonly onDeleteComment: (commentKey: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState("");
  const submit = async () => {
    const content = draft.trim();
    if (!content) return;
    try {
      await props.onCreateComment(content);
      setDraft("");
    } catch {
      // The hook owns the visible error. Keep the draft for a retry.
    }
  };

  return (
    <div className="space-y-4">
      <form
        className="rounded-lg border border-border bg-background p-3"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Textarea
          aria-label="Add a comment"
          value={draft}
          maxLength={2_000}
          disabled={props.commenting}
          onChange={(event) => setDraft(event.currentTarget.value)}
          placeholder="Add a comment…"
          size="sm"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">{draft.length}/2000</span>
          <Button
            type="submit"
            size="sm"
            className={RESPONSIVE_TEXT_BUTTON_CLASS}
            disabled={!draft.trim() || props.commenting || draft.length > 2_000}
          >
            <MessageSquareIcon aria-hidden />
            {props.commenting ? "Adding…" : "Comment"}
          </Button>
        </div>
      </form>

      {props.loading ? (
        <p className="text-sm text-muted-foreground">Loading comments…</p>
      ) : props.comments.length > 0 ? (
        <ol className="space-y-4">
          {props.comments.map((comment) => (
            <ProjectTaskCommentRow
              key={comment.commentKey}
              comment={comment}
              pending={props.commenting}
              onUpdate={props.onUpdateComment}
              onDelete={props.onDeleteComment}
            />
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">No comments yet.</p>
      )}
    </div>
  );
}
